/**
 * Frontend Calculation Utilities for Supply Chain Management
 * Performs all calculations that were previously done on the backend
 */

// Types for raw data from backend
export const SAFETY_STOCK_DAYS = 14;

export interface RawInventoryRecord {
  sku: string;
  platform: string;
  on_hand_quantity: number;
  available_quantity: number;
  allocated_quantity: number;
  total_quantity: number;
  last_synced: string;
}

export interface RawSalesRecord {
  date: string;
  sku: string;
  quantity: number;
  country: string;
}

export interface RawProductData {
  product_name: string;
  skus: string[];
  sku_count: number;
  raw_inventory_data: RawInventoryRecord[];
  manufacturing_time_days: number;
  shipping_time_days: number;
  safety_stock_days: number;
  platforms_used: string[];
  data_source: string;
  calculation_method: string;
  warehouse: string;
  timestamp: string;
}

export interface RawSkuSalesData {
  sku_id: string;
  warehouse: string;
  raw_sales_records: RawSalesRecord[];
  record_count: number;
  data_type: string;
  calculation_method: string;
  calculation_formulas: {
    drr_7: string;
    drr_14: string;
    drr_30: string;
    weighted_drr: string;
  };
  timestamp: string;
}

// Types for calculated results
export interface CalculatedProductMetrics {
  product_name: string;
  sku_count: number;
  current_stock: number;
  available_stock: number;
  allocated_stock: number;
  daily_run_rate_7: number;
  daily_run_rate_14: number;
  daily_run_rate_30: number;
  weighted_daily_run_rate: number;
  demand_growth_rate: number;
  forecast_stock_90_days: number;
  target_stock_104_days: number;
  oos_date: string;
  days_until_oos: number;
  reorder_flagged: boolean;
  minimum_reorder_amount: number;
  order_cutoff_date: string;
  manufacturing_time_days: number;
  shipping_time_days: number;
  minimum_stock_quantity: number;
  maximum_stock_quantity: number;
  stock_level_status: string;
  platforms_used: string[];
  data_source: string;
  sku_breakdown?: {
    sku: string;
    current_stock: number;
    available_stock: number;
    allocated_stock: number;
    daily_run_rate_7: number;
    daily_run_rate_14: number;
    daily_run_rate_30: number;
    weighted_daily_run_rate: number;
    platform: string;
    data_source: string;
    last_synced: string;
  }[];
}

export interface CalculationCache {
  [key: string]: {
    data: any;
    timestamp: number;
    ttl: number;
  };
}

/**
 * Client-side caching for calculated results
 */
export class FrontendCalculationCache {
  private cache: CalculationCache = {};
  private readonly defaultTTL = 20 * 60 * 1000; // 20 minutes

  set(key: string, data: any, ttl: number = this.defaultTTL): void {
    this.cache[key] = {
      data,
      timestamp: Date.now(),
      ttl,
    };
  }

  get(key: string): any | null {
    const cached = this.cache[key];
    if (!cached) {
      return null;
    }

    const isExpired = Date.now() - cached.timestamp > cached.ttl;
    if (isExpired) {
      delete this.cache[key];
      return null;
    }


    return cached.data;
  }

  clear(): void {
    const keyCount = Object.keys(this.cache).length;
    this.cache = {};
  }

  // Add method to check cache status
  getCacheStatus(): { keys: string[], totalItems: number } {
    const keys = Object.keys(this.cache);
    return { keys, totalItems: keys.length };
  }

  getKeys(): string[] {
    return Object.keys(this.cache);
  }
}

// Global cache instance
export const calculationCache = new FrontendCalculationCache();

/**
 * Calculate daily run rates from raw sales data
 * Uses consistent day boundaries (midnight) to prevent timestamp-dependent calculation variations
 */
export function calculateDailyRunRates(salesRecords: RawSalesRecord[]): {
  drr_7: number;
  drr_14: number;
  drr_30: number;
  weighted_drr: number;
} {

  // CHECK FOR PRE-CALCULATED DRR VALUES (from BigQuery cache)
  if (salesRecords && salesRecords.length > 0 && (salesRecords[0] as any).use_precalculated_drr) {
    const precalc = salesRecords[0] as any;
    return {
      drr_7: precalc.drr_7 || 0,
      drr_14: precalc.drr_14 || 0,
      drr_30: precalc.drr_30 || 0,
      weighted_drr: precalc.weighted_drr || 0
    };
  }

  if (salesRecords?.length > 0) {
    const totalQuantity = salesRecords.reduce((sum, record) => sum + (record.quantity || 0), 0);
  }

  if (!salesRecords || salesRecords.length === 0) {
    return { drr_7: 0, drr_14: 0, drr_30: 0, weighted_drr: 0 };
  }

  // Sort by date descending (most recent first)
  const sortedRecords = salesRecords
    .filter(record => record.date && record.quantity >= 0)
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  if (sortedRecords.length === 0) {
    return { drr_7: 0, drr_14: 0, drr_30: 0, weighted_drr: 0 };
  }

  //  Use the LATEST DATA DATE as reference (instead of today) to handle data lag
  // If sales data is 5 days old, we calculate from that date, not from today
  const latestRecordDate = sortedRecords.length > 0 ? new Date(sortedRecords[0].date) : new Date();
  latestRecordDate.setHours(0, 0, 0, 0); // Normalize to start of day

  const now = new Date();
  now.setHours(0, 0, 0, 0);

  const dataLagDays = Math.floor((now.getTime() - latestRecordDate.getTime()) / (24 * 60 * 60 * 1000));

  // Calculate date boundaries from the latest data date (not today)
  const sevenDaysAgo = new Date(latestRecordDate.getTime() - 7 * 24 * 60 * 60 * 1000);
  const fourteenDaysAgo = new Date(latestRecordDate.getTime() - 14 * 24 * 60 * 60 * 1000);
  const thirtyDaysAgo = new Date(latestRecordDate.getTime() - 30 * 24 * 60 * 60 * 1000);

  // Calculate daily averages for different periods
  const last7Days = sortedRecords.filter(record => new Date(record.date) >= sevenDaysAgo);
  const last14Days = sortedRecords.filter(record => new Date(record.date) >= fourteenDaysAgo);
  const last30Days = sortedRecords.filter(record => new Date(record.date) >= thirtyDaysAgo);

  //  Business requirement = Total Sales / Period (in days), NOT number of records
  let drr_7 = last7Days.length > 0
    ? last7Days.reduce((sum, record) => sum + record.quantity, 0) / 7
    : 0;

  let drr_14 = last14Days.length > 0
    ? last14Days.reduce((sum, record) => sum + record.quantity, 0) / 14
    : 0;

  let drr_30 = last30Days.length > 0
    ? last30Days.reduce((sum, record) => sum + record.quantity, 0) / 30
    : 0;

  // Remove any demo/backfill/random fallbacks. Use only calculated values.

  // Weighted average: 70% recent (7 days), 20% medium (14 days), 10% longer term (30 days)
  const weighted_drr = (drr_7 * 0.7) + (drr_14 * 0.2) + (drr_30 * 0.1);

  return {
    drr_7: Math.round(drr_7 * 100) / 100,
    drr_14: Math.round(drr_14 * 100) / 100,
    drr_30: Math.round(drr_30 * 100) / 100,
    weighted_drr: Math.round(weighted_drr * 100) / 100,
  };
}

/**
 * Calculate demand growth rate from sales data
 */
export function calculateDemandGrowthRate(salesRecords: RawSalesRecord[]): number {
  // YOUR SPEC: "Apply a line of best fit to daily units sold for a given sku over the last 30 days"
  // Uses most recent 30 days OF AVAILABLE DATA (handles stale data gracefully)

  if (!salesRecords || salesRecords.length < 10) {
    return 0; // Need at least 10 days for meaningful trend
  }

  // First aggregate all sales by day, then take most recent 30 days of AVAILABLE data
  // This handles cases where BigQuery data is stale (e.g., last sync was 40+ days ago)
  const allDailySales = new Map<string, number>();
  salesRecords.forEach(record => {
    const dateKey = record.date;
    allDailySales.set(dateKey, (allDailySales.get(dateKey) || 0) + record.quantity);
  });

  // Sort all dates and take most recent 30 days of data
  const allSortedDates = Array.from(allDailySales.keys()).sort().reverse(); // Most recent first
  const recentDates = allSortedDates.slice(0, 30); // Take up to 30 most recent days

  if (recentDates.length < 5) {
    return 0;
  }

  // Log data freshness info
  const mostRecentDate = recentDates[0];
  const oldestInRange = recentDates[recentDates.length - 1];

  // Convert to array of [dayIndex, quantity] for line of best fit
  // Reverse back to chronological order for proper regression
  const chronologicalDates = recentDates.reverse();
  const dataPoints: [number, number][] = [];

  chronologicalDates.forEach((date, index) => {
    dataPoints.push([index, allDailySales.get(date) || 0]);
  });

  // Calculate line of best fit using least squares regression
  // y = mx + b, where m is the slope (daily growth rate)
  const n = dataPoints.length;
  const sumX = dataPoints.reduce((sum, [x]) => sum + x, 0);
  const sumY = dataPoints.reduce((sum, [, y]) => sum + y, 0);
  const sumXY = dataPoints.reduce((sum, [x, y]) => sum + x * y, 0);
  const sumXX = dataPoints.reduce((sum, [x]) => sum + x * x, 0);

  // Slope formula: m = (n*ΣXY - ΣX*ΣY) / (n*ΣXX - (ΣX)²)
  const denominator = n * sumXX - sumX * sumX;
  if (denominator === 0) {
    return 0;
  }

  const slope = (n * sumXY - sumX * sumY) / denominator;
  const avgY = sumY / n;

  // Convert slope to daily growth rate percentage
  // If avgY is 0, we can't calculate percentage growth
  if (avgY === 0) {
    return 0;
  }

  const dailyGrowthRate = slope / avgY;

  // Cap extreme growth rates to prevent unrealistic forecasts.
  // Same bound as backend ADDG_LIMIT: +/-20% per day.
  return Math.max(-0.20, Math.min(0.20, dailyGrowthRate));
}

/**
 * Calculate inventory metrics from raw inventory data
 */
export function calculateInventoryMetrics(inventoryRecords: RawInventoryRecord[]): {
  current_stock: number;
  available_stock: number;
  allocated_stock: number;
  platforms_used: string[];
} {
  if (!inventoryRecords || inventoryRecords.length === 0) {
    return {
      current_stock: 0,
      available_stock: 0,
      allocated_stock: 0,
      platforms_used: [],
    };
  }

  let totalOnHand = 0;
  let totalAvailable = 0;
  let totalAllocated = 0;
  const platforms = new Set<string>();

  inventoryRecords.forEach(record => {
    // For OpenBorder, use available_quantity as the primary stock value
    // For other platforms, use on_hand_quantity
    const stockQuantity = record.platform === 'openborder'
      ? (record.available_quantity || 0)
      : (record.on_hand_quantity || 0);

    totalOnHand += stockQuantity;
    totalAvailable += record.available_quantity || 0;
    totalAllocated += record.allocated_quantity || 0;

    if (record.platform) {
      platforms.add(record.platform);
    }
  });

  return {
    current_stock: totalOnHand,
    available_stock: totalAvailable,
    allocated_stock: totalAllocated,
    platforms_used: Array.from(platforms),
  };
}

/**
 * Calculate forecast stock for 90 days based on run rate and growth.
 * ADDG is a linear per-day fraction, so day-t demand is DRR * max(0, 1 + ADDG * t).
 */
// A linear multiplier (1 + ADDG*t) always crosses zero for any negative trend,
// collapsing the forecast tail to zero well before day 90 even for small slopes.
// Clamping matches the backend (growth_forecast.py): no SKU forecasts zero
// demand, and no mild trend gets extrapolated into an absurd multiplier.
const GROWTH_FACTOR_FLOOR = 0.5;
const GROWTH_FACTOR_CEIL = 1.5;

export function calculateForecastStock(
  currentStock: number,
  dailyRunRate: number,
  demandGrowthRate: number = 0
): number {
  if (dailyRunRate <= 0) {
    return 0;
  }

  // demandGrowthRate is a per-day fraction (ADDG), not a percentage.
  if (Math.abs(demandGrowthRate) < 1e-8) {
    return Math.round(90 * dailyRunRate);
  }

  let forecastRequirement = 0;
  for (let t = 1; t <= 90; t++) {
    const growthFactor = Math.max(GROWTH_FACTOR_FLOOR, Math.min(GROWTH_FACTOR_CEIL, 1 + demandGrowthRate * t));
    forecastRequirement += dailyRunRate * growthFactor;
  }

  return Math.round(forecastRequirement);
}

/**
 * Calculate out-of-stock date
 */
export function calculateOOSDate(
  currentStock: number,
  dailyRunRate: number,
  demandGrowthRate: number = 0
): string {
  if (dailyRunRate <= 0) {
    return "Unknown"; // No consumption
  }

  // If there is demand (DRR > 0) but no stock, we are already OOS today
  if (currentStock <= 0) {
    //  Use consistent date (start of day) for stable calculations
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today.toISOString().split('T')[0];
  }

  // Apply growth rate to run rate
  const adjustedRunRate = dailyRunRate * Math.max(0, 1 + demandGrowthRate);

  if (adjustedRunRate <= 0) {
    return "No consumption";
  }

  const daysUntilOOS = Math.floor(currentStock / adjustedRunRate);
  //  Use consistent date (start of day) for stable calculations
  const oosDate = new Date();
  oosDate.setHours(0, 0, 0, 0); // Normalize to start of day
  oosDate.setDate(oosDate.getDate() + daysUntilOOS);

  return oosDate.toISOString().split('T')[0]; // YYYY-MM-DD format
}

/**
 * Calculate reorder logic and cutoff dates
 * Business requirement: Min Reorder Amount = FSRN90 - SA - CS
 * Reorder Flagged = Today > (OOSD - MT - ST - 28)
 */
export function calculateReorderLogic(
  currentStock: number,
  allocatedStock: number,
  dailyRunRate: number,
  manufacturingDays: number,
  shippingDays: number,
  safetyStockDays: number = 14,
  demandGrowthRate: number = 0,
  quantitiesInProgress: number = 0 // Add quantities in progress parameter
): {
  reorder_flagged: boolean;
  minimum_reorder_amount: number;
  order_cutoff_date: string;
  days_until_oos: number;
  days_until_cutoff: number;
} {
  if (dailyRunRate <= 0) {
    // No demand → cannot calculate reorder urgency
    return {
      reorder_flagged: false,
      minimum_reorder_amount: 0,
      order_cutoff_date: "N/A",
      days_until_oos: 0,
      days_until_cutoff: 0,
    };
  }

  // Calculate Target Stock (104 days as per specification)
  const targetStock = Math.round(104 * dailyRunRate * Math.max(0, 1 + demandGrowthRate));

  // Total Amount Required = Target Stock - Current Stock - Allocated Stock - Quantities In Progress
  const minimumReorderAmount = Math.max(0, targetStock - currentStock - allocatedStock - quantitiesInProgress);

  // Calculate Out of Stock Date (OOSD)
  const adjustedRunRate = dailyRunRate * Math.max(0, 1 + demandGrowthRate);
  const availableStock = Math.max(0, currentStock - allocatedStock);
  const daysUntilOOSRaw = availableStock > 0 && adjustedRunRate > 0
    ? availableStock / adjustedRunRate
    : 0;
  const daysUntilOOS = Math.floor(daysUntilOOSRaw);

  // Business Formula: Reorder Flagged = Today > (OOSD - MT - ST - 28)
  // MT = Manufacturing Time (25 days), ST = Shipping Time (45 days), Buffer = 28 days
  const totalBufferDays = manufacturingDays + shippingDays + safetyStockDays; // 25 + 45 + 28 = 98 days
  // If we have demand but zero available stock, daysUntilOOS is 0 → flag reorder
  const reorderFlagged = daysUntilOOSRaw <= totalBufferDays;
  const daysUntilCutoff = Math.max(0, daysUntilOOS - manufacturingDays - shippingDays);

  // Calculate Order Cutoff Date = OOSD - MT - ST (correct business logic)
  let orderCutoffDate = "N/A";
  if (reorderFlagged && daysUntilOOS > 0) {
    // Calculate OOS Date first
    const oosDate = new Date();
    oosDate.setHours(0, 0, 0, 0); // Normalize to start of day
    oosDate.setDate(oosDate.getDate() + Math.floor(daysUntilOOS));

    // Calculate Cutoff Date = OOSD - MT - ST
    const cutoffDate = new Date(oosDate);
    cutoffDate.setDate(cutoffDate.getDate() - manufacturingDays - shippingDays);

    orderCutoffDate = cutoffDate.toISOString().split('T')[0]; // YYYY-MM-DD format
  }

  return {
    reorder_flagged: reorderFlagged,
    minimum_reorder_amount: reorderFlagged ? minimumReorderAmount : 0,
    order_cutoff_date: orderCutoffDate,
    days_until_oos: daysUntilOOS,
    days_until_cutoff: daysUntilCutoff,
  };
}

/**
 * Main function to calculate all metrics for a product
 */
export function calculateProductMetrics(
  rawProductData: RawProductData,
  salesRecords: RawSalesRecord[] = [],
  quantitiesInProgress: number = 0
): CalculatedProductMetrics {
  try {
    // Safety check for rawProductData
    if (!rawProductData) {
      throw new Error('rawProductData is null or undefined');
    }

    // Calculate inventory metrics
    const inventoryMetrics = calculateInventoryMetrics(rawProductData.raw_inventory_data);

    // Calculate run rates
    const runRates = calculateDailyRunRates(salesRecords);

    // Calculate demand growth
    const demandGrowth = calculateDemandGrowthRate(salesRecords);

    // Calculate forecast
    const forecastStock = calculateForecastStock(
      inventoryMetrics.current_stock,
      runRates.weighted_drr,
      demandGrowth
    );

    // Calculate OOS date
    const availableStockForTiming = Math.max(0, inventoryMetrics.current_stock - inventoryMetrics.allocated_stock);
    const oosDate = calculateOOSDate(
      availableStockForTiming,
      runRates.weighted_drr,
      demandGrowth
    );

    // Calculate reorder logic (quantities in progress passed from Dashboard)
    const reorderLogic = calculateReorderLogic(
      inventoryMetrics.current_stock,
      inventoryMetrics.allocated_stock,
      runRates.weighted_drr,
      rawProductData.manufacturing_time_days || 25,
      rawProductData.shipping_time_days || 45,
      rawProductData.safety_stock_days || 28,
      demandGrowth,
      quantitiesInProgress // Use the passed parameter instead of hardcoded 0
    );

    // SPECIFICATION COMPLIANCE: Calculate 104-day target and stock level constraints
    const targetStock104Days = Math.round(104 * runRates.weighted_drr); // 90 days order + 14 buffer
    const minimumStockQuantity = Math.round(14 * runRates.weighted_drr); // 2 weeks minimum
    const maximumStockQuantity = Math.round(120 * runRates.weighted_drr); // 4 months maximum

    // Calculate days until OOS
    const daysUntilOOS = reorderLogic.days_until_oos;

    // Check stock level compliance status
    const totalStock = inventoryMetrics.current_stock + inventoryMetrics.allocated_stock;
    let stockLevelStatus = "compliant";
    if (totalStock < minimumStockQuantity) {
      stockLevelStatus = "below_minimum";
    } else if (totalStock > maximumStockQuantity) {
      stockLevelStatus = "above_maximum";
    }

    return {
      product_name: rawProductData.product_name,
      sku_count: rawProductData.sku_count,
      current_stock: inventoryMetrics.current_stock,
      available_stock: inventoryMetrics.available_stock,
      allocated_stock: inventoryMetrics.allocated_stock,
      daily_run_rate_7: runRates.drr_7,
      daily_run_rate_14: runRates.drr_14,
      daily_run_rate_30: runRates.drr_30,
      weighted_daily_run_rate: runRates.weighted_drr,
      demand_growth_rate: demandGrowth,
      forecast_stock_90_days: forecastStock,
      target_stock_104_days: targetStock104Days,
      oos_date: oosDate,
      days_until_oos: daysUntilOOS,
      reorder_flagged: reorderLogic.reorder_flagged,
      minimum_reorder_amount: reorderLogic.minimum_reorder_amount,
      order_cutoff_date: reorderLogic.order_cutoff_date,
      manufacturing_time_days: rawProductData.manufacturing_time_days,
      shipping_time_days: rawProductData.shipping_time_days,
      minimum_stock_quantity: minimumStockQuantity,
      maximum_stock_quantity: maximumStockQuantity,
      stock_level_status: stockLevelStatus,
      platforms_used: inventoryMetrics.platforms_used,
      data_source: rawProductData.data_source,
      sku_breakdown: (rawProductData as any).sku_breakdown,
    };

  } catch (error) {
    console.error(`❌ Error in calculateProductMetrics for ${rawProductData?.product_name || 'Unknown'}:`, error);
    // Return default safe values
    return {
      product_name: rawProductData?.product_name || 'Unknown Product',
      sku_count: rawProductData?.sku_count || 0,
      current_stock: 0,
      available_stock: 0,
      allocated_stock: 0,
      daily_run_rate_7: 0,
      daily_run_rate_14: 0,
      daily_run_rate_30: 0,
      weighted_daily_run_rate: 0,
      demand_growth_rate: 0,
      forecast_stock_90_days: 0,
      target_stock_104_days: 0,
      oos_date: "N/A",
      days_until_oos: 0,
      reorder_flagged: false,
      minimum_reorder_amount: 0,
      order_cutoff_date: "N/A",
      manufacturing_time_days: 25,
      shipping_time_days: 45,
      minimum_stock_quantity: 0,
      maximum_stock_quantity: 0,
      stock_level_status: "unknown",
      platforms_used: [],
      data_source: "error",
    };
  }
}

/**
 * Batch calculate metrics for multiple products
 */
export async function batchCalculateMetrics(
  rawProductsData: RawProductData[],
  getSalesData: (skus: string[], warehouse: string) => Promise<RawSalesRecord[]>
): Promise<CalculatedProductMetrics[]> {
  const results: CalculatedProductMetrics[] = [];

  for (const productData of rawProductsData) {
    try {
      // Get sales data for all SKUs of this product
      const salesRecords = await getSalesData(productData.skus, productData.warehouse);

      // Calculate metrics for this product
      const metrics = calculateProductMetrics(productData, salesRecords);
      results.push(metrics);

    } catch (error) {
      console.error(`Error calculating metrics for ${productData.product_name}:`, error);

      // Return basic metrics with zero values on error
      results.push({
        product_name: productData.product_name,
        sku_count: productData.sku_count,
        current_stock: 0,
        available_stock: 0,
        allocated_stock: 0,
        daily_run_rate_7: 0,
        daily_run_rate_14: 0,
        daily_run_rate_30: 0,
        weighted_daily_run_rate: 0,
        demand_growth_rate: 0,
        forecast_stock_90_days: 0,
        target_stock_104_days: 0,
        oos_date: "Unknown",
        days_until_oos: 0,
        reorder_flagged: false,
        minimum_reorder_amount: 0,
        order_cutoff_date: "N/A",
        manufacturing_time_days: productData.manufacturing_time_days,
        shipping_time_days: productData.shipping_time_days,
        minimum_stock_quantity: 0,
        maximum_stock_quantity: 0,
        stock_level_status: "unknown",
        platforms_used: [],
        data_source: "error",
      });
    }
  }

  return results;
}

/**
 * Utility function to format currency
 */
export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
}

/**
 * Utility function to format dates
 */
export function formatDate(dateString: string): string {
  if (!dateString || dateString === "N/A" || dateString === "Unknown") {
    return dateString;
  }

  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return dateString;
  }
}

/**
 * Utility function to calculate days until a date
 */
export function getDaysUntil(dateString: string): number {
  if (!dateString || dateString === "N/A" || dateString === "Unknown") {
    return -1;
  }

  try {
    const targetDate = new Date(dateString);
    targetDate.setHours(0, 0, 0, 0); // Normalize to start of day

    // Use consistent date (start of day) for stable calculations
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Normalize to start of day

    const diffTime = targetDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  } catch {
    return -1;
  }
}
